import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { authApi } from 'lib/api'
import { Mail, Lock, AlertCircle, CheckCircle, ArrowLeft, AlertTriangle } from 'lucide-react'

export function Recover2FA() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const linkToken = searchParams.get('token') || ''
  const linkEmail = searchParams.get('email') || ''
  const openedFromLink = Boolean(linkToken && linkEmail)

  const [step, setStep] = useState<'request' | 'verify' | 'expired'>(openedFromLink ? 'verify' : 'request')
  const [email, setEmail] = useState(linkEmail)
  const [token, setToken] = useState(linkToken)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [checkingLink, setCheckingLink] = useState(openedFromLink)

  useEffect(() => {
    if (!openedFromLink) return

    let cancelled = false
    const checkLink = async () => {
      try {
        await authApi.checkRecoveryToken(linkToken, linkEmail)
        if (cancelled) return
        setStep('verify')
        setEmail(linkEmail)
        setToken(linkToken)
      } catch (error: any) {
        if (cancelled) return
        if (error?.response?.status === 400) {
          setStep('expired')
          setError(t('auth.recoveryLinkExpired', 'This recovery link has expired.'))
        } else {
          setStep('verify')
          setError(error?.response?.data?.error || t('auth.recoveryLinkExpired', 'This recovery link has expired.'))
        }
      } finally {
        if (!cancelled) setCheckingLink(false)
      }
    }

    checkLink()
    return () => {
      cancelled = true
    }
  }, [openedFromLink, linkToken, linkEmail, t])

  const handleRequestRecovery = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    try {
      await authApi.request2FARecovery(email)
      setSuccess(t('auth.recoveryEmailSent', 'A recovery link was sent to this email.'))
      setError('')
    } catch (error: any) {
      const status = error.response?.status
      const apiError = error.response?.data?.error
      if (status === 404) {
        setError(t('auth.noAccountForEmail', 'No account found for this email.'))
      } else if (apiError === '2FA is not enabled for this account') {
        setError(t('auth.twoFactorNotEnabled', '2FA is not enabled for this account'))
      } else {
        setError(apiError || t('auth.recoveryEmailFailed', 'Failed to send recovery email. Please try again.'))
      }
      setSuccess('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    try {
      const response = await authApi.verifyRecoveryToken(token, email)
      setSuccess(response.data.message || '2FA has been disabled. You can now log in.')
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(`/login?email=${encodeURIComponent(email)}`)
      }, 3000)
    } catch (error: any) {
      if (error?.response?.status === 400) {
        setStep('expired')
        setError(t('auth.recoveryLinkExpired', 'This recovery link has expired.'))
      } else {
        setError(error.response?.data?.error || t('auth.recoveryLinkExpired', 'This recovery link has expired.'))
      }
      setSuccess('')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-background rounded-lg shadow-lg p-8 space-y-6">
          {checkingLink ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : step !== 'expired' && (
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-primary/10 rounded-full mb-4">
                <Lock className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">
                {step === 'request' ? 'Recover 2FA Access' : 'Verify Recovery Token'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {step === 'request'
                  ? 'Enter your email to receive a recovery link'
                  : 'Verify your recovery token to disable 2FA'}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && !checkingLink && (
            <div className="flex items-center space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <span className="text-sm text-green-600 dark:text-green-400">{success}</span>
            </div>
          )}

          {/* Request Step */}
          {step === 'request' && (
            <form onSubmit={handleRequestRecovery} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-800 dark:text-yellow-400">
                    A recovery link will be sent to your email. The link will expire in 24 hours.
                  </p>
                </div>
              </div>

              <Button type="submit" className="w-full h-11" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Recovery Link'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          )}

          {/* Verify Step */}
          {step === 'verify' && !checkingLink && (
            <form onSubmit={handleVerifyToken} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email-verify" className="text-sm font-medium text-muted-foreground">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email-verify"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="token" className="text-sm font-medium text-muted-foreground">
                  Recovery Token
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="token"
                    type="password"
                    placeholder="Enter recovery token from email"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    className="pl-10 h-11 font-mono"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-800 dark:text-red-400">
                    <strong>Warning:</strong> This will permanently disable 2FA for your account. You can re-enable it from Settings after logging in.
                  </p>
                </div>
              </div>

              <Button type="submit" className="w-full h-11" disabled={isLoading || !token}>
                {isLoading ? 'Verifying...' : 'Disable 2FA and Recover Account'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStep('request')
                  setToken('')
                }}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Request New Recovery Link
              </Button>
            </form>
          )}

          {step === 'expired' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          )}

          {/* Info */}
          {step !== 'expired' && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                If you have backup codes, you can use them during login instead.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

